package com.evoapps.evoshift.domain.usecase.daily

import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.usecase.finance.CalculateDailyRecordUseCase

/**
 * The manager enters a day's takings (net, SH-70): the day's record is recomputed with them, so its
 * actual labour % follows (as on the Daily Records screen). The plan, the comments (unless new ones
 * are given), a gross figure entered before and the lock are kept; a closed (locked) day is final
 * ([com.evoapps.evoshift.domain.model.DayClosedException]). A day never stored is created.
 */
class SubmitDailyRevenueUseCase(
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase
) {
    /** The takings of [date] at [workplaceId] (the dashboard's prompt, SH-68; the carousel, SH-71). */
    suspend operator fun invoke(
        workplaceId: String,
        rosterId: String,
        date: String,
        actualNetto: Double,
        comments: String? = null
    ): Result<DailyRecord> = runCatching {
        require(actualNetto >= 0.0) { "Takings cannot be negative" }
        val record = calculateDailyRecordUseCase.recompute(workplaceId, rosterId, date) { kept ->
            kept.withTypedTakings(actualNetto).copy(comments = comments ?: kept.comments)
        }.getOrThrow()
        financialRepository.saveDailyRecord(record).getOrThrow()
        record
    }

    /** The takings of a stored daily record. */
    suspend operator fun invoke(
        dailyRecordId: String,
        actualAmount: Double,
        comments: String
    ): Result<Unit> = runCatching {
        val record = financialRepository.getDailyRecord(dailyRecordId).getOrThrow()
        invoke(record.workplaceId, record.rosterId, record.date, actualAmount, comments).getOrThrow()
    }
}
